import {
    approvedConditionDocumentExists,
    calculateNetPrepaidBalanceOnSettlement,
} from './utilities';
import IApplication from '~Api/Application/IApplication';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import { IDictionary } from '~utilities/IDictionary';
import _ from 'lodash';
import dayjs from 'dayjs';
import IReferralPartner from '~Api/ReferralPartner/IReferralPartner';
import IApplicationDisbursement from '~Api/Application/IApplicationDisbursement';
import IProperty from '~Api/Deal/IProperty';
import { propertyRequiresInsurance } from '~Deals/utilities';
import ExtensionTypeEnum from '~Api/Application/ExtensionTypeEnum';
import BorrowerTypeEnum from '~Api/Application/BorrowerTypeEnum';
import DisbursementTypeEnum from '~Api/Application/DisbursementTypeEnum';
import BrokerApprovalStatusEnum from '~Api/Broker/ApprovalStatusEnum';
import PartnerApprovalStatusEnum from '~Api/ReferralPartner/ApprovalStatusEnum';
import PropertyPurposeEnum from '~Api/Deal/PropertyPurposeEnum';
import LoanPurposeEnum from '~Api/Deal/LoanPurposeEnum';
import PartnerCommissionFormatEnum from '~Api/Application/PartnerCommissionFormatEnum';
import BrokerCommissionFormatEnum from '~Api/Application/BrokerCommissionFormatEnum';
import BorrowerCategoryEnum from '~Api/Application/BorrowerCategoryEnum';
import { validateEmail } from '~utilities/validators';

export function getDestinationStatusDocumentErrors (
    application: IApplication,
    applicationEntityConditions: IDictionary<IDictionary<ICondition>>,
    conditionDocumentsByConditionUuid: IDictionary<IConditionDocument[]>,
    destinationStatus: WorkflowStatusEnum,
): string[] {
    const documentErrors: string[] = [];
    const entityNames: IDictionary<string> = {
        [application.uuid]: 'Application',
    };
    _.each(application.borrowers, (borrower: IBorrower) => {
        entityNames[borrower.uuid] = borrower.dealBorrower.formattedName;
    });
    _.each(application.properties, (property: IApplicationProperty) => {
        entityNames[property.uuid] = property.dealProperty.formattedAddress;
    });

    _.each(applicationEntityConditions, (conditions: IDictionary<ICondition>, entityUuid: string): void => {
        _.each(conditions, (condition: ICondition): void => {
            if (
                condition.requiredWorkflowStatuses.includes(destinationStatus) &&
                !approvedConditionDocumentExists(conditionDocumentsByConditionUuid[condition.uuid] || [])
            ) {
                documentErrors.push(`${entityNames[entityUuid]} - ${condition.name}`);
            }
        });
    });

    return documentErrors;
}

export function validatePropertyValuationInspectionDate(
    applicationProperty: IApplicationProperty,
    informationErrors: string[],
): void {
    if (!applicationProperty.valuationInspectionDate) {
        informationErrors.push(`${applicationProperty.dealProperty.formattedAddress} - Valuation Inspection Date`);
    } else if (dayjs(applicationProperty.valuationInspectionDate) < dayjs().subtract(90, 'days')) {
        informationErrors.push(`${applicationProperty.dealProperty.formattedAddress} - Valuation Inspection Date Expired`);
    }
}

export function getDestinationStatusInformationErrors(
    application: IApplication,
    disbursements: IDictionary<IApplicationDisbursement>,
    destinationStatus: WorkflowStatusEnum,
    partners: IDictionary<IReferralPartner>,
): string[] {
    const informationErrors: string[] = [];
    const isRenewal: boolean = ExtensionTypeEnum.Renewal === application.extensionType;

    // Statuses can be moved/jumped arbritrarily all later statuses must check / error on earlier statuses
    switch (destinationStatus) {
        case WorkflowStatusEnum.Warehoused: {
            _.each(application.borrowers, (applicationBorrower: IBorrower) => {
                if (BorrowerTypeEnum.Individual === applicationBorrower.dealBorrower.type && !applicationBorrower.dealBorrower.residencyStatus) {
                    informationErrors.push(`${applicationBorrower.dealBorrower.formattedName} - Residency Status`);
                }
            });

            _.each(application.properties, (applicationProperty: IApplicationProperty) => {
                const property: IProperty = applicationProperty.dealProperty;
                if (propertyRequiresInsurance(property)) {
                    if (!property.insuranceExpiryDate || dayjs(property.insuranceExpiryDate) < dayjs()) {
                        informationErrors.push(`${property.formattedAddress} - Insurance Date`);
                    }
                    if (!property.insuranceReplacementValue) {
                        informationErrors.push(`${property.formattedAddress} - Insurance Replacement Value`);
                    }
                }
                validatePropertyValuationInspectionDate(applicationProperty, informationErrors);
            });

            const prepaidInterestDisbursements: IApplicationDisbursement[] = _.filter(disbursements, { type: DisbursementTypeEnum.PrepaidInterest });
            if (prepaidInterestDisbursements.length === 0) {
                informationErrors.push('Application - Prepaid interest disbursement not provided');
            } else if (prepaidInterestDisbursements.length > 1) {
                informationErrors.push('Application - There should only be one prepaid interest disbursement');
            }

            const disbursementsTotal: number = _.reduce(disbursements, (sum: number, loopDisbursement: IApplicationDisbursement) => sum + loopDisbursement.amount, 0);

            if (Math.round(disbursementsTotal * 100) !== Math.round(application.loanAmount * 100)) {
                informationErrors.push('Application - Disbursements total does not match the loan amount');
            }

            if (application.payoutFigure) {
                const properties: IDictionary<IProperty> = {};
                _.each(application.properties, (property: IApplicationProperty) => {
                    properties[property.dealPropertyUuid] = property.dealProperty;
                });
                const netPrepaidBalanceOnSettlement: number = application.netBalance || calculateNetPrepaidBalanceOnSettlement(application, properties);
                if (netPrepaidBalanceOnSettlement < 0) {
                    informationErrors.push('Application - Net payout amount can\'t be lower than $0');
                }
            }
        }
        // eslint-disable-next-line no-fallthrough
        case WorkflowStatusEnum.Settlement: {
            if (!application.primaryBorrowerUuid) {
                informationErrors.push('Application - Primary Borrower');
            }

            const validEmailRecipients: IBorrower[] = _.filter(application.borrowers, (borrower: IBorrower) => borrower.email && validateEmail(borrower.email));
            if (validEmailRecipients.length === 0) {
                informationErrors.push('Application - A Borrower with a valid email address is required');
            }

            _.each(application.borrowers, (applicationBorrower: IBorrower) => {
                if (BorrowerTypeEnum.Individual === applicationBorrower.dealBorrower.type && !applicationBorrower.dealBorrower.residencyStatus) {
                    informationErrors.push(`${applicationBorrower.dealBorrower.formattedName} - Residency Status`);
                }
            });

            _.each(application.properties, (applicationProperty: IApplicationProperty) => {
                const property: IProperty = applicationProperty.dealProperty;

                validatePropertyValuationInspectionDate(applicationProperty, informationErrors);

                if (propertyRequiresInsurance(property)) {
                    if (!property.insuranceExpiryDate) {
                        informationErrors.push(`${property.formattedAddress} - Insurance Expiry Date`);
                    }
                }
            });
        }
        // eslint-disable-next-line no-fallthrough
        case WorkflowStatusEnum.LegalDocuments: {
            // We don't use solicitors for extensions
            if (!isRenewal && !application.solicitorUuid) {
                informationErrors.push('Application - Application Solicitor');
            }

            if (!isRenewal) {
                _.each(application.borrowers, (applicationBorrower: IBorrower) => {
                    if (BorrowerTypeEnum.Individual === applicationBorrower.dealBorrower.type && !applicationBorrower.dealBorrower.residencyStatus) {
                        informationErrors.push(`${applicationBorrower.dealBorrower.formattedName} - Residency Status`);
                    }
                });
            }
        }
        // eslint-disable-next-line no-fallthrough
        case WorkflowStatusEnum.FormalApproval: {
            // We don't involve brokers or partners in renewals
            if (!isRenewal) {
                const referralPartner: IReferralPartner = application.deal.referralPartnerUuid && partners[application.deal.referralPartnerUuid];

                const hasApprovedBroker: boolean = application.deal.broker && application.deal.broker.approvalStatus === BrokerApprovalStatusEnum.Approved;
                const hasApprovedReferralPartner: boolean = referralPartner && referralPartner.approvalStatus === PartnerApprovalStatusEnum.Approved;

                const isBroker: boolean = !!application.deal.broker || application.deal.isBroker;
                const isReferralPartner: boolean = !!application.deal.referralPartnerUuid || application.deal.isReferralPartner;

                if (isBroker && !hasApprovedBroker) {
                    informationErrors.push('Application - Approved Broker required');
                }

                if (isBroker && ((application.brokerCommissionFormat === BrokerCommissionFormatEnum.Percentage && !application.brokerCommissionPercentage) || (application.brokerCommissionFormat === BrokerCommissionFormatEnum.Dollars && !application.brokerCommissionDollars) || !application.brokerCommissionFormat)) {
                    informationErrors.push('Application - Broker Commission required');
                }

                if (isReferralPartner && !hasApprovedReferralPartner) {
                    informationErrors.push('Application - Approved Referral Partner required');
                }

                if (isReferralPartner && ((application.partnerCommissionFormat === PartnerCommissionFormatEnum.Percentage && !application.partnerCommissionPercentage) || (application.partnerCommissionFormat === PartnerCommissionFormatEnum.Dollars && !application.partnerCommissionDollars) || !application.partnerCommissionFormat)) {
                    informationErrors.push('Application - Partner Commission required');
                }

                if (application.brokerageFee && !hasApprovedBroker && !hasApprovedReferralPartner) {
                    informationErrors.push('Application - Brokerage Fee requires an approved Broker or Referral Partner');
                }
            }

            if (!application.codeType) {
                informationErrors.push('Application - Loan Type');
            }
            if (!application.mortgageType) {
                informationErrors.push('Application - Mortgage Type');
            }

            if (!application.loanPurpose) {
                informationErrors.push('Application - Loan Purpose');
            } else {
                // Don't check properties on renewals as we already have them
                if (!isRenewal) {
                    switch (application.loanPurpose) {
                        case LoanPurposeEnum.BridgingLoan:
                            if (!_.some(application.properties, { dealProperty: { purpose: PropertyPurposeEnum.Purchase } }) && !_.some(application.properties, { dealProperty: { purpose: PropertyPurposeEnum.Sell } })) {
                                informationErrors.push('Bridging Loan Application - Purchase or Sell property required');
                            }
                            break;
                        case LoanPurposeEnum.DebtConsolidation:
                            if (!_.some(application.properties, { dealProperty: { purpose: PropertyPurposeEnum.Security } })) {
                                informationErrors.push('Debt Consolidation Loan Application - Security property required');
                            }
                            break;
                        case LoanPurposeEnum.PurchaseNew:
                            if (!_.some(application.properties, { dealProperty: { purpose: PropertyPurposeEnum.Purchase } })) {
                                informationErrors.push('Purchase Loan Application - Purchase property required');
                            }
                            break;
                        case LoanPurposeEnum.Refinance:
                            if (!_.some(application.properties, { dealProperty: { purpose: PropertyPurposeEnum.Refinance } })) {
                                informationErrors.push('Refinance Loan Application - Refinance property required');
                            }
                            break;
                        case LoanPurposeEnum.RenovateOrBuild:
                            if (!_.some(application.properties, { dealProperty: { purpose: PropertyPurposeEnum.Develop } }) && !_.some(application.properties, { dealProperty: { purpose: PropertyPurposeEnum.PurchaseDevelop } })) {
                                informationErrors.push('Develop Loan Application - Develop or Purchase and Develop property required');
                            }
                            break;
                    }
                }
            }
            if (!application.repaymentStrategy) {
                informationErrors.push('Application - Repayment Strategy');
            }
            if (!application.secondaryRepaymentStrategy) {
                informationErrors.push('Application - Secondary Repayment Strategy');
            }
            if (!application.disbursalType) {
                informationErrors.push('Application - Disbursal Type');
            }

            // Don't check borrowers on renewals as we already have them
            if (!isRenewal) {
                application.borrowers.forEach((borrower: IBorrower) => {
                    if (borrower.dealBorrower.type === BorrowerTypeEnum.Individual && !borrower.dealBorrower.incomeType) {
                        informationErrors.push(`${borrower.dealBorrower.formattedName} - Income Type`);
                    }

                    if ([ BorrowerTypeEnum.Company, BorrowerTypeEnum.Individual ].includes(borrower.dealBorrower.type) && !borrower.creditScore) {
                        informationErrors.push(`${borrower.dealBorrower.formattedName} - Credit Score`);
                    }

                    if (BorrowerTypeEnum.Individual === borrower.dealBorrower.type && !borrower.dealBorrower.residencyStatus) {
                        informationErrors.push(`${borrower.dealBorrower.formattedName} - Residency Status`);
                    }

                    switch (borrower.dealBorrower.type) {
                        case BorrowerTypeEnum.Company:
                            if (_.filter(application.borrowers, (loopBorrower: IBorrower) => loopBorrower.uuid !== borrower.uuid && loopBorrower.dealBorrower.category === BorrowerCategoryEnum.Guarantor).length === 0) {
                                informationErrors.push('Company Borrower Present - Guarantor required');
                            }
                            break;
                        case BorrowerTypeEnum.Trust:
                            if (_.filter(application.borrowers, (loopBorrower: IBorrower) => loopBorrower.uuid !== borrower.uuid && loopBorrower.dealBorrower.category === BorrowerCategoryEnum.Trustee).length === 0) {
                                informationErrors.push('Trust Borrower Present - Trustee required');
                            }
                            break;
                    }
                });
            }

            // Don't check properties on renewals as we already have them
            if (!isRenewal) {
                _.each(application.properties, (applicationProperty: IApplicationProperty) => {
                    const property: IProperty = applicationProperty.dealProperty;
                    if (!property.purpose) {
                        informationErrors.push(`${property.formattedAddress} - Purpose`);
                    }
                    if (!property.strataType) {
                        informationErrors.push(`${property.formattedAddress} - Strata Type`);
                    }
                    if (!property.tenancyType) {
                        informationErrors.push(`${property.formattedAddress} - Tenancy Type`);
                    }
                    validatePropertyValuationInspectionDate(applicationProperty, informationErrors);
                    if (!property.zoneType) {
                        informationErrors.push(`${property.formattedAddress} - Zoning`);
                    }
                    if (!property.folioNumber && !property.lotNumber && !property.planNumber && !property.volumeNumber) {
                        informationErrors.push(`${property.formattedAddress} - Title Information`);
                    }
                    if (!property.state) {
                        informationErrors.push(`${property.formattedAddress} - State`);
                    }
                    if (!property.postcode) {
                        informationErrors.push(`${property.formattedAddress} - Postcode`);
                    }
                    if (!property.lotSizeSquareMetres) {
                        informationErrors.push(`${property.formattedAddress} - Lot Size`);
                    }
                    if (property.dwellingCount === null) {
                        informationErrors.push(`${property.formattedAddress} - Number of Dwellings`);
                    }
                });
            }
        }
        // eslint-disable-next-line no-fallthrough
        case WorkflowStatusEnum.Underwriting:
        case WorkflowStatusEnum.ConditionalApproval:
        case WorkflowStatusEnum.New:
    }

    return informationErrors;
}